/**
 * Shared parsing utilities for ingestion job results.
 *
 * Both useIngestionHydration (one-shot rehydration on page refresh) and
 * useValidationAndProfiling (active polling during validation) need to
 * interpret the same backend job response structures. These functions
 * centralise that logic so changes to the API schema only need updating
 * in one place.
 */
import {
  DataProfile,
  mapTabularProfileToDataProfile,
} from "datasets/pages/tables/components/file-validation/types";
import {
  DataIngestionResponse,
  JobResponse,
  TaskResult,
  TabularProfileTaskMessage,
  type FileTypeValidationTaskMessage,
  type FileRowValidationTaskMessage,
} from "datasets/pages/tables/types/interfaces/ingestion-job";

// ---------------------------------------------------------------------------
// TODO(SCDM-2560): Remove this helper once the backend always returns parsed objects.
// https://manifoldai.atlassian.net/browse/SCDM-2560
// ---------------------------------------------------------------------------

/**
 * Safely parse job results.data, handling both a JSON string (current API)
 * and a pre-parsed object (upcoming API change).
 *
 * This is the JSON deserialization boundary — the `as Record<string, unknown>`
 * cast is unavoidable here since we're converting untyped JSON into our domain types.
 */
export function parseResultsData(data: unknown): Record<string, unknown> | null {
  if (data == null) {
    return null;
  }
  // Already a parsed object from the API
  if (typeof data === "object") {
    return data as Record<string, unknown>;
  }
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validation job parsing
// ---------------------------------------------------------------------------

export interface ParsedValidationResults {
  /** Path to the _VALID_ROWS file produced by row validation */
  validatedFilePath: string | null;
  /** Path to the _INVALID_ROWS file produced by row validation */
  invalidRowsFilePath: string | null;
  /** Path to the _ROW_DETAILS.json file with per-row validation metadata */
  rowDetailsFilePath: string | null;
  /** Detected file type (e.g. "csv", "tsv"), lowercased */
  fileType: string | null;
  /** Number of invalid rows reported by row validation */
  errorRows: number;
  /** Whether any validation task has state === "FAILED" */
  hasErrors: boolean;
  /** Whether any validation task has state === "WARNING" */
  hasWarnings: boolean;
  /** The raw tasks array (null if parsing failed or no tasks present) */
  tasks: TaskResult[] | null;
  /** Job-level error message when the job fails without producing tasks */
  errorMessage: string | null;
}

/**
 * Parse a validation job's results.data JSON and extract the core fields
 * that both hooks need: file type, validated file path, error counts,
 * and status flags.
 *
 * Returns the raw `tasks` array so consumers can do additional UI-specific
 * mapping (e.g. building ValidationJob[] cards) without re-parsing.
 */
export function parseValidationJobResults(job: JobResponse): ParsedValidationResults {
  const defaults: ParsedValidationResults = {
    validatedFilePath: null,
    invalidRowsFilePath: null,
    rowDetailsFilePath: null,
    fileType: null,
    errorRows: 0,
    hasErrors: false,
    hasWarnings: false,
    tasks: null,
    errorMessage: null,
  };

  const parsed = parseResultsData(job.results?.data);
  if (!parsed) {
    return defaults;
  }

  // Job failed with an error message but no tasks
  if (!parsed.tasks && parsed.error) {
    return { ...defaults, hasErrors: true, errorMessage: String(parsed.error) };
  }

  if (!parsed.tasks) {
    return defaults;
  }

  // Boundary cast: parsed.tasks comes from JSON deserialization and conforms
  // to the TaskResult[] shape defined by the backend contract.
  const tasks = parsed.tasks as TaskResult[];

  const fileTypeTask = tasks.find(
    (t): t is TaskResult & { message: FileTypeValidationTaskMessage } =>
      t.message.task_name === "FileTypeValidationTaskMessage"
  );
  const rowTask = tasks.find(
    (t): t is TaskResult & { message: FileRowValidationTaskMessage } =>
      t.message.task_name === "FileRowValidationTaskMessage"
  );

  return {
    validatedFilePath: rowTask?.message.valid_rows_filepath ?? null,
    invalidRowsFilePath: rowTask?.message.invalid_rows_filepath ?? null,
    rowDetailsFilePath: rowTask?.message.row_validation_details ?? null,
    fileType: fileTypeTask?.message.file_type?.toLowerCase() ?? null,
    errorRows: rowTask?.message.invalid_row_count ?? 0,
    hasErrors: tasks.some((t) => t.state === "FAILED"),
    hasWarnings: tasks.some((t) => t.state === "WARNING"),
    tasks,
    errorMessage: null,
  };
}

// ---------------------------------------------------------------------------
// Profiling job parsing
// ---------------------------------------------------------------------------

/**
 * Normalize profiling job results into a tasks array, handling both the
 * current PipelineSummary shape and the legacy flat TaskOutput shape.
 *
 * New shape (PipelineSummary): { tasks: [{ state, file, message }, ...] }
 * Legacy shape (flat TaskOutput): { state, file, message }
 *
 * TODO(SCDM-2832): Remove legacy branch once all old profiling jobs have aged out.
 */
export function normalizeProfilingTasks(parsed: Record<string, unknown>): TaskResult[] | null {
  if (parsed.tasks) {
    return parsed.tasks as TaskResult[];
  }
  if (parsed.message) {
    return [{ state: parsed.state, message: parsed.message } as TaskResult];
  }
  return null;
}

/**
 * Parse a profiling job's results.data JSON and produce a DataProfile.
 *
 * Returns null when the job failed, has no results, or the results
 * cannot be parsed. Consumers handle their own error state management
 * (e.g. ProfilingError, isProfiling flags).
 */
export function parseProfilingJobResults(job: JobResponse, errorRows: number): DataProfile | null {
  const parsed = parseResultsData(job.results?.data);
  if (!parsed) {
    return null;
  }
  if (parsed.error) {
    return null;
  }

  const tasks = normalizeProfilingTasks(parsed);
  if (!tasks) {
    return null;
  }
  if (tasks.some((t) => t.state === "FAILED")) {
    return null;
  }

  const profilingTask = tasks.find(
    (t): t is TaskResult & { message: TabularProfileTaskMessage } =>
      t.message.task_name === "TabularProfileTaskMessage"
  );
  if (!profilingTask) {
    return null;
  }

  return mapTabularProfileToDataProfile(profilingTask.message, errorRows);
}

// ---------------------------------------------------------------------------
// Source file URI recovery
// ---------------------------------------------------------------------------

/**
 * Recover the original source file URI from job results.
 *
 * We must always prefer the user's original selection (the file they uploaded or
 * picked from cloud storage), never the derived _VALID_ROWS file. Profiling runs
 * on validation output, so profiling results contain the valid rows path — never
 * use that for "source file" display.
 *
 * Fallback chain (original source only):
 *   1. Validation job arguments: `file_uris[0]` — input to validation, always original
 *   2. Ingestion record: `ingestion.source_file`
 *   3. Validation job results: `parsed.tasks[0].file` — first task runs on original
 *   4. Other job arguments (validation job may not exist yet)
 *   5. Profiling results — last resort only; prefer null over wrong path in practice
 */
export function recoverSourceFileUri(
  jobs: JobResponse[],
  ingestion?: DataIngestionResponse
): string | null {
  const profilingJob = jobs.find((j) => j.job_type === "profiling");
  const validationJob = jobs.find((j) => j.job_type === "validation");

  // 1. Validation job arguments: always the original source (input to validation)
  const validationFileUri = validationJob?.arguments?.file_uris?.[0];
  if (validationFileUri) {
    return validationFileUri;
  }

  // 2. Ingestion record
  if (ingestion?.source_file) {
    return ingestion.source_file;
  }

  // 3. Validation job parsed results: first task (FileTypeValidation) runs on original
  const validationParsed = parseResultsData(validationJob?.results?.data);
  const tasksArray = validationParsed?.tasks as Array<Record<string, unknown>> | undefined;
  const firstTaskFile = tasksArray?.[0]?.file;
  if (firstTaskFile && typeof firstTaskFile === "string") {
    return firstTaskFile;
  }

  // 4. Other job arguments (e.g. profiling-only when validation hasn't run)
  for (const job of jobs) {
    const fileUri = job.arguments?.file_uris?.[0];
    if (fileUri) {
      return fileUri;
    }
  }

  // 5. Profiling parsed — WARNING: this is often _VALID_ROWS, not the original.
  // Only use as last resort when nothing else is available.
  // Handles both PipelineSummary (tasks[0].file) and legacy flat (top-level file).
  const profilingParsed = parseResultsData(profilingJob?.results?.data);
  const profilingTasks = profilingParsed?.tasks as Array<Record<string, unknown>> | undefined;
  const profilingFile = profilingTasks?.[0]?.file ?? profilingParsed?.file;
  if (profilingFile && typeof profilingFile === "string") {
    return profilingFile;
  }

  return null;
}

// ---------------------------------------------------------------------------
// S3 URI helpers
// ---------------------------------------------------------------------------

/**
 * Strip the s3://bucket/ prefix from a URI to get the plain object key.
 * If the value isn't an S3 URI, returns it as-is.
 * Accepts null/undefined for convenience and returns null in that case.
 */
export function stripS3Prefix(uri: string | null | undefined): string | null {
  if (!uri) {
    return null;
  }
  if (uri.startsWith("s3://")) {
    const withoutProtocol = uri.slice(5); // remove "s3://"
    const slashIndex = withoutProtocol.indexOf("/");
    return slashIndex !== -1 ? withoutProtocol.slice(slashIndex + 1) : withoutProtocol;
  }
  return uri;
}
